import { useCallback, useEffect, useRef, useState } from "react";
import type { CmmdDeviceRegistrySnapshot } from "@cmmd-forge/contracts";

import { cmmdApiClient } from "../../environments/cmmdApi";

export const CMMD_RUNTIME_REGISTRY_REQUEST_TIMEOUT_MS = 15_000;

export interface CmmdRuntimeRegistrySnapshotState {
  readonly runtimeRegistry: CmmdDeviceRegistrySnapshot | null;
  readonly runtimeRegistryError: string | null;
  readonly isLoadingRuntimeRegistry: boolean;
  readonly refreshRuntimeRegistry: () => void;
}

export function useCmmdRuntimeRegistrySnapshot(): CmmdRuntimeRegistrySnapshotState {
  const [runtimeRegistry, setRuntimeRegistry] = useState<CmmdDeviceRegistrySnapshot | null>(null);
  const [runtimeRegistryError, setRuntimeRegistryError] = useState<string | null>(null);
  const [isLoadingRuntimeRegistry, setIsLoadingRuntimeRegistry] = useState(false);
  const isMountedRef = useRef(false);
  const requestIdRef = useRef(0);
  const requestInFlightRef = useRef(false);
  const requestAbortRef = useRef<AbortController | null>(null);

  const refreshRuntimeRegistry = useCallback(() => {
    if (requestInFlightRef.current) return;
    requestInFlightRef.current = true;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const controller = new AbortController();
    requestAbortRef.current = controller;
    setIsLoadingRuntimeRegistry(true);
    setRuntimeRegistryError(null);

    const shouldApplyResult = () => isMountedRef.current && requestIdRef.current === requestId;
    const timeoutId = window.setTimeout(() => {
      controller.abort();
      if (!shouldApplyResult()) return;
      requestIdRef.current += 1;
      requestInFlightRef.current = false;
      requestAbortRef.current = null;
      setRuntimeRegistryError(
        "Device connection refresh timed out. Showing last known connections.",
      );
      setIsLoadingRuntimeRegistry(false);
    }, CMMD_RUNTIME_REGISTRY_REQUEST_TIMEOUT_MS);

    void cmmdApiClient
      .getDeviceRegistry({ signal: controller.signal })
      .then((registry) => {
        if (!shouldApplyResult()) return;
        setRuntimeRegistry(registry);
      })
      .catch((error: unknown) => {
        if (!shouldApplyResult()) return;
        const message =
          error instanceof Error ? error.message : "Could not load CMMD runtime registry.";
        setRuntimeRegistryError(message);
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
        if (requestAbortRef.current === controller) requestAbortRef.current = null;
        requestInFlightRef.current = false;
        if (!shouldApplyResult()) return;
        setIsLoadingRuntimeRegistry(false);
      });
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    refreshRuntimeRegistry();
    const refreshWhenActive = () => {
      if (document.visibilityState === "visible") refreshRuntimeRegistry();
    };
    const intervalId = window.setInterval(refreshWhenActive, 10_000);
    window.addEventListener("focus", refreshWhenActive);
    window.addEventListener("online", refreshWhenActive);
    document.addEventListener("visibilitychange", refreshWhenActive);
    return () => {
      isMountedRef.current = false;
      requestIdRef.current += 1;
      requestAbortRef.current?.abort();
      requestAbortRef.current = null;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshWhenActive);
      window.removeEventListener("online", refreshWhenActive);
      document.removeEventListener("visibilitychange", refreshWhenActive);
    };
  }, [refreshRuntimeRegistry]);

  return {
    runtimeRegistry,
    runtimeRegistryError,
    isLoadingRuntimeRegistry,
    refreshRuntimeRegistry,
  };
}
